import React, { useContext, useState } from "react";
import classNames from "classnames";
import { uniqueId } from "lodash";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { ReportSettingsModel } from "../../exportModels";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";

interface ReportSettingsFlyoutProps {
  currentReportSettings: ReportSettingsModel;
  getSelectionDisplayName: (rs: ReportSettingsModel) => string;
  handleCreateNewReportSettingsClick: () => void;
  isNameOk: (name: string) => boolean;
  reportSettingsList: ReportSettingsModel[];
  reportSettingsHasChanges: boolean;
  reportSettingsHasInvalidChanges: boolean;
  saveClicked: boolean;
  setCurrentReportSettings: (rs: ReportSettingsModel) => void;
  setReportSettingsHasChanges: (hasChanges: boolean) => void;
  setReportSettingsHasInvalidChanges: (hasInvalidChanges: boolean) => void;
  setReportSettingsList: (rs: ReportSettingsModel[]) => void;
  setSaveClicked: (clicked: boolean) => void;
}

export const ReportSettingsFlyout: React.FC<React.PropsWithChildren<ReportSettingsFlyoutProps>> = (props: ReportSettingsFlyoutProps) => {
  const reportSettingsFlyoutId = uniqueId("flyout-");
  const flyoutContext = useContext(FlyoutContext);
  const [idsToUpdate, setIdsToUpdate] = useState<number[]>([]);
  const [idsToDelete, setIdsToDelete] = useState<number[]>([]);
  const {
    currentReportSettings,
    getSelectionDisplayName,
    handleCreateNewReportSettingsClick,
    isNameOk,
    reportSettingsHasChanges,
    reportSettingsHasInvalidChanges,
    reportSettingsList,
    saveClicked,
    setCurrentReportSettings,
    setReportSettingsHasChanges,
    setReportSettingsHasInvalidChanges,
    setReportSettingsList,
    setSaveClicked,
  } = props;

  const handleRename = (id: number, name: string) => {
    setSaveClicked(false);
    if (currentReportSettings && currentReportSettings.id === id)
      setCurrentReportSettings({ ...currentReportSettings, name });
    updateReportSettingsList(id, "name", name);
    setReportSettingsHasChanges(true);
    setReportSettingsHasInvalidChanges(reportSettingsList.some(rs => !isNameOk(rs.name)));
    setIdsToUpdate(prevState => [...new Set([...prevState, id])]);
  }

  const handleDeleteReportSettingsClick = async (id: number) => {
    const shouldDelete = !reportSettingsList.find(rs => rs.id === id).displayToDelete;
    updateReportSettingsList(id, "displayToDelete", shouldDelete);
    setReportSettingsHasChanges(true);
    setSaveClicked(false);
    if (shouldDelete)
      setIdsToDelete(prevState => [...new Set([...prevState, id])]);
    else
      setIdsToDelete(prevState => [...new Set([...prevState].filter(delId => delId !== id))]);
  }

  const handleSaveClick = async () => {
    setSaveClicked(true);
    setReportSettingsHasChanges(false);
    if (idsToUpdate.length > 0)
      await helpers.saveUpdatedReportSettingsList(reportSettingsList.filter(rs => idsToUpdate.includes(rs.id)));
    if (idsToDelete.length > 0) {
      const deleted = await helpers.deleteReportSettings(idsToDelete);
      if (deleted) {
        if (currentReportSettings && idsToDelete.includes(currentReportSettings.id))
          setCurrentReportSettings(undefined);
        setReportSettingsList(reportSettingsList.filter(rs => !idsToDelete.includes(rs.id)));
      }
    }
  }

  const updateReportSettingsList = (id: number, key: string, value: (string | boolean)) => {
    const isDisplayKey = key === "displayNameInput" || key === "displayActions";
    const updateValueByKey = <T, K extends keyof T>(obj: T, key: K, value: T[K]) => obj[key] = value;

    updateValueByKey<ReportSettingsModel, keyof ReportSettingsModel>(
      reportSettingsList[reportSettingsList.findIndex(rs => rs.id === id)],
      key as keyof ReportSettingsModel,
      value);
    if (!isDisplayKey)
      setReportSettingsHasChanges(true);
    setReportSettingsList(reportSettingsList);
  }

  return (
    <>
      <div className="row">
        <div className="col-lg-12 text-center">
          <ul className="list-group">
            <li className="list-group-header">Name</li>
            {(reportSettingsList && reportSettingsList.length > 0)
              ? (reportSettingsList.map((rs) => {
                const row = rs.displayNameInput
                  ? <div className={classNames("form-group w-100 mb-0", { "has-error": !isNameOk(rs.name) })}>
                    <input
                      autoFocus
                      className="form-control"
                      id={reportSettingsFlyoutId}
                      onBlur={() => updateReportSettingsList(rs.id, "displayNameInput", false)}
                      onChange={(e) => handleRename(rs.id, e.target.value)}
                      type="text"
                      value={rs.name}
                    />
                    {
                      !isNameOk(rs.name) &&
                      <label className="mt-2 text-danger text-start float-start"><i className="fa fa-exclamation-triangle"></i>&nbsp;&nbsp;Name must be between 1 and 255 characters.</label>
                    }
                  </div>
                  : <>
                    <div className="flex-grow-1 text-start text-truncate">
                      {rs.displayToDelete
                        ? <s>{getSelectionDisplayName(rs)}</s>
                        : getSelectionDisplayName(rs)
                      }
                    </div>
                    {rs.displayActions &&
                      <div className="btn-group btn-group-sm flex-shrink-1">
                        <QESButton
                          qesTestId="report-settings-flyout_rename"
                          qesType="secondary"
                          onClick={() => updateReportSettingsList(rs.id, "displayNameInput", true)}
                        >
                          <b>Rename</b>
                        </QESButton>
                        <QESButton
                          qesTestId="report-settings-flyout_delete"
                          qesType="secondary-danger"
                          onClick={() => handleDeleteReportSettingsClick(rs.id)}
                        >
                          <b>Delete</b>
                        </QESButton>
                      </div>
                    }
                  </>

                return (
                  <li
                    key={rs.id}
                    className="list-group-item list-group-item-action align-items-center"
                    onMouseEnter={() => updateReportSettingsList(rs.id, "displayActions", true)}
                    onMouseLeave={() => updateReportSettingsList(rs.id, "displayActions", false)}
                    style={{ minHeight: "55px" }}
                  >
                    {row}
                  </li>
                );
              }))
              : <li className="list-group-item">No records found</li>
            }
          </ul>
          <QESButton
            qesTestId="report-settings-flyout_create-new"
            qesType="secondary"
            className="float-end"
            onClick={() => { flyoutContext.handleCloseFlyout(); handleCreateNewReportSettingsClick(); }}
          >
            <b>Create New</b>
          </QESButton>
        </div>
      </div>
      <FlyoutFooter
        submitButton={saveClicked
          ? <QESButton qesTestId="report-settings-flyout_settings-saved" qesType="primary" disabled={true}>Settings Saved!</QESButton>
          : <QESButton qesTestId="report-settings-flyout_save-settings" disabled={!reportSettingsHasChanges || reportSettingsHasInvalidChanges} onClick={() => handleSaveClick()}>Save Settings</QESButton>
        }
      />
    </>
  );
}